import { useCallback, useEffect, useRef, useState } from 'react'

type UseResizableSidebarOptions = {
  initialWidth?: number
  minWidth?: number
  maxWidth?: number
}

/**
 * Drives a draggable divider between the call's video area and its side panel.
 * The divider is grabbed via pointer events; the panel width is derived from the
 * pointer's distance to the container's right edge and clamped to [min, max].
 * Keyboard arrows resize too (accessibility).
 */
export function useResizableSidebar(
  containerRef: React.RefObject<HTMLElement | null>,
  { initialWidth = 360, minWidth = 280, maxWidth = 620 }: UseResizableSidebarOptions = {},
) {
  const [width, setWidthState] = useState(initialWidth)
  const [dragging, setDragging] = useState(false)
  const widthRef = useRef(width)
  widthRef.current = width

  const clamp = useCallback(
    (value: number) => Math.min(maxWidth, Math.max(minWidth, value)),
    [maxWidth, minWidth],
  )

  const setWidth = useCallback((value: number) => setWidthState(clamp(value)), [clamp])

  const onPointerDown = useCallback(
    (event: React.PointerEvent) => {
      event.preventDefault()
      setDragging(true)
      const container = containerRef.current
      const rightEdge = container?.getBoundingClientRect().right ?? window.innerWidth

      const onMove = (e: PointerEvent) => {
        setWidthState(clamp(rightEdge - e.clientX))
      }
      const onUp = () => {
        setDragging(false)
        window.removeEventListener('pointermove', onMove)
        window.removeEventListener('pointerup', onUp)
      }
      window.addEventListener('pointermove', onMove)
      window.addEventListener('pointerup', onUp)
    },
    [clamp, containerRef],
  )

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        setWidthState(clamp(widthRef.current + 24))
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        setWidthState(clamp(widthRef.current - 24))
      }
    },
    [clamp],
  )

  // Keep the panel within bounds if the window/container shrinks.
  useEffect(() => {
    const onResize = () => setWidthState((w) => clamp(w))
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [clamp])

  const dividerProps = {
    onPointerDown,
    onKeyDown,
    role: 'separator' as const,
    'aria-orientation': 'vertical' as const,
    'aria-valuenow': Math.round(width),
    'aria-valuemin': minWidth,
    'aria-valuemax': maxWidth,
    tabIndex: 0,
  }

  return { width, setWidth, dragging, dividerProps }
}
